import { Button } from '../Button/Button';
import { useReadOnly } from '../ReadOnly/ReadOnly';
import { SparkIcon } from '../SparkIcon';
import { Spinner } from '../Spinner';
import { TooltipTrigger } from '../Tooltip';
import type { BulkActionsBarProps } from './BulkActionsBar.types';
import './BulkActionsBar.css';

/**
 * BulkActionsBar — shown when one or more table rows are selected.
 *
 * Layout:
 * ```
 * With actions:    [X of Y selected] [Select All] | [action buttons…] | [Deselect All]  →  [endContent]
 * Without actions: [X of Y selected] [Select All] | [Deselect All]  →  [endContent]
 * ```
 *
 * `showDeselectAll={false}` omits the Deselect All link and its trailing divider.
 * `variant="inline"` shrinks the bar to content (no full-width row) so it can
 * sit on an existing heading without pushing the page down.
 *
 * - `actions` — primary buttons (Review, Archive, Replace, …) rendered between
 *   the selection links, each as a `secondary tiny` Button.
 * - `endContent` — right-aligned slot for secondary actions such as Delete or Download.
 * - No confirmation modals — call handlers fire directly; wrap with modals in the consumer.
 */
export function BulkActionsBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  showDeselectAll = true,
  actions = [],
  endContent,
  disabled = false,
  className = '',
  variant = 'default',
}: BulkActionsBarProps) {
  const { readOnly } = useReadOnly();
  const rootClass = [
    'spark-bulk-actions-bar',
    variant === 'inline' ? 'spark-bulk-actions-bar--inline' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      {/* Selection count */}
      <p className="spark-bulk-actions-bar-label">
        {selectedCount} of {totalCount} selected
      </p>

      {/* Select All */}
      <button
        type="button"
        className="spark-bulk-actions-bar-link"
        onClick={disabled ? undefined : onSelectAll}
        aria-disabled={disabled}
        disabled={disabled}
      >
        Select All
      </button>

      {/* Divider — always shown; a second one appears after actions when actions exist */}
      <span className="spark-bulk-actions-bar-divider" aria-hidden="true" />

      {/* Primary action buttons */}
      {actions.length > 0 && (
        <>
          <div className="spark-bulk-actions-bar-actions">
            {actions.map((action) => {
              const startIcon = action.loading
                ? <Spinner size="small" />
                : action.iconSlug
                  ? <SparkIcon slug={action.iconSlug} size="small" />
                  : undefined;
              const btn = (
                <Button
                  key={action.label}
                  variant="secondary"
                  size="tiny"
                  type="button"
                  disabled={disabled || action.disabled || action.loading}
                  disableWhenReadOnly={action.disableWhenReadOnly}
                  startIcon={startIcon}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              );

              return action.tooltip && !(readOnly && action.disableWhenReadOnly) ? (
                <TooltipTrigger key={action.label} text={action.tooltip}>
                  {btn}
                </TooltipTrigger>
              ) : btn;
            })}
          </div>

          {showDeselectAll ? (
            <span className="spark-bulk-actions-bar-divider" aria-hidden="true" />
          ) : null}
        </>
      )}

      {showDeselectAll ? (
        <button
          type="button"
          className="spark-bulk-actions-bar-link"
          onClick={disabled ? undefined : onDeselectAll}
          aria-disabled={disabled}
          disabled={disabled}
        >
          Deselect All
        </button>
      ) : null}

      {/* Right-aligned end content (Delete, Download, etc.) */}
      {endContent != null && (
        <div className="spark-bulk-actions-bar-end">{endContent}</div>
      )}
    </div>
  );
}
